import "../css/header.scss";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4 4" />
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.8 5.8a5.1 5.1 0 0 0-7.2 0L12 7.4l-1.6-1.6a5.1 5.1 0 0 0-7.2 7.2L12 21l8.8-8a5.1 5.1 0 0 0 0-7.2Z" />
  </svg>
);

const BagIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 8.5h14l-1 12H6l-1-12Z" />
    <path d="M9 9V6a3 3 0 0 1 6 0v3" />
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 6.5h18M3 12h18M3 17.5h18" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m5 5 14 14M19 5 5 19" />
  </svg>
);

function Header() {
  const commerce = useContext(CartContext) || {};
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const searchButtonRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");

  const cartCount =
    commerce.cartCount ??
    commerce.cartItems?.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0
    ) ??
    0;
  const wishlistCount =
    commerce.wishlistCount ?? commerce.wishlistItems?.length ?? 0;

  const shopAllLink =
    siteConfig.nav.find((item) => item.href === "/shop") || siteConfig.nav[0];
  const categoryLinks = siteConfig.nav.filter((item) =>
    item.href.includes("category=")
  );
  const storyLinks = siteConfig.nav.filter(
    (item) => item.href !== shopAllLink.href && !item.href.includes("category=")
  );

  const shippingLabel = new Intl.NumberFormat(siteConfig.locale, {
    style: "currency",
    currency: siteConfig.currency,
  }).format(siteConfig.shippingRate);

  const closePanels = useCallback(() => {
    setSearchOpen(false);
    setMobileOpen(false);
  }, []);

  const handleNavigation = useCallback(() => {
    closePanels();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [closePanels]);

  useEffect(() => {
    closePanels();
  }, [location.pathname, location.search, closePanels]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const handleScroll = () => closePanels();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [closePanels]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      if (searchOpen) {
        setSearchOpen(false);
        searchButtonRef.current?.focus();
      }

      if (mobileOpen) {
        setMobileOpen(false);
        mobileMenuButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileOpen, searchOpen]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const term = query.trim();

    navigate(term ? `/shop?q=${encodeURIComponent(term)}` : "/shop");
    closePanels();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleSearch = () => {
    setSearchOpen((open) => !open);
    setMobileOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileOpen((open) => !open);
    setSearchOpen(false);
  };

  const navClassName = (href) => {
    const [pathname, search = ""] = href.split("?");
    const targetParams = new URLSearchParams(search);
    const currentParams = new URLSearchParams(location.search);
    const category = targetParams.get("category");
    const active =
      location.pathname === pathname &&
      (!category || currentParams.get("category") === category);

    return `site-header__nav-link${active ? " is-active" : ""}`;
  };

  return (
    <header className="site-header">
      <div className="site-header__announcement" role="status">
        <div className="site-header__announcement-inner">
          <span>Standard shipping {shippingLabel}</span>
          <span className="site-header__announcement-detail">
            Packed with care for peak flavor
          </span>
        </div>
      </div>

      <div className="site-header__main">
        <div className="site-header__container site-header__main-inner">
          <button
            ref={mobileMenuButtonRef}
            className="site-header__icon-button site-header__menu-button"
            type="button"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={toggleMobileMenu}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </button>

          <Link
            className="site-header__brand"
            to="/"
            aria-label={siteConfig.logo.ariaLabel}
            onClick={handleNavigation}
          >
            <span className="site-header__wordmark" aria-hidden="true">
              <span className="site-header__monogram">{siteConfig.logo.monogram}</span>
              <span className="site-header__wordmark-name">{siteConfig.logo.text}</span>
            </span>
            <span className="site-header__tagline">
              {siteConfig.logo.tagline}
            </span>
          </Link>

          <div className="site-header__actions">
            <button
              ref={searchButtonRef}
              className="site-header__action"
              type="button"
              aria-label={searchOpen ? "Close search" : "Open search"}
              aria-expanded={searchOpen}
              aria-controls="site-search"
              onClick={toggleSearch}
            >
              <SearchIcon />
              <span className="site-header__action-label">Search</span>
            </button>

            <Link
              className="site-header__action"
              to="/shop?wishlist=true"
              aria-label={`Wishlist with ${wishlistCount} item${
                wishlistCount === 1 ? "" : "s"
              }`}
              onClick={handleNavigation}
            >
              <span className="site-header__action-icon">
                <HeartIcon />
                {wishlistCount > 0 && (
                  <span className="site-header__badge" aria-hidden="true">
                    {wishlistCount > 99 ? "99+" : wishlistCount}
                  </span>
                )}
              </span>
              <span className="site-header__action-label">Wishlist</span>
            </Link>

            <Link
              className="site-header__action"
              to="/cartpage"
              aria-label={`Shopping cart with ${cartCount} item${
                cartCount === 1 ? "" : "s"
              }`}
              onClick={handleNavigation}
            >
              <span className="site-header__action-icon">
                <BagIcon />
                {cartCount > 0 && (
                  <span className="site-header__badge" aria-hidden="true">
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </span>
              <span className="site-header__action-label">Cart</span>
            </Link>
          </div>
        </div>
      </div>

      <nav className="site-header__desktop-nav" aria-label="Primary navigation">
        <div className="site-header__container">
          <ul className="site-header__nav-list">
            <li className="site-header__nav-item site-header__shop-item">
              <Link
                className={navClassName(shopAllLink.href)}
                to={shopAllLink.href}
                onClick={handleNavigation}
              >
                Shop
                <span className="site-header__chevron" aria-hidden="true">
                  ↓
                </span>
              </Link>
              <div className="site-header__mega-menu">
                <div>
                  <p className="site-header__mega-kicker">The good stuff</p>
                  <p className="site-header__mega-title">
                    Build a table worth gathering around.
                  </p>
                  <Link
                    className="site-header__mega-cta"
                    to={shopAllLink.href}
                    onClick={handleNavigation}
                  >
                    Explore everything
                  </Link>
                </div>
                <ul aria-label="Shop categories">
                  {categoryLinks.map((item) => (
                    <li key={item.href}>
                      <Link to={item.href} onClick={handleNavigation}>
                        {item.label}
                        <span aria-hidden="true">→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </li>

            {[...categoryLinks, ...storyLinks].map((item) => (
              <li className="site-header__nav-item" key={item.href}>
                <Link
                  className={navClassName(item.href)}
                  to={item.href}
                  onClick={handleNavigation}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li className="site-header__nav-item site-header__more-item">
              <button className="site-header__nav-link site-header__more-button" type="button">
                More <span className="site-header__chevron" aria-hidden="true">↓</span>
              </button>
              <ul className="site-header__more-menu" aria-label="More information">
                {siteConfig.policyNav.map((item) => (
                  <li key={item.href}>
                    <Link to={item.href} onClick={handleNavigation}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </div>
      </nav>

      {searchOpen && (
        <section
          className="site-header__search-panel"
          id="site-search"
          aria-label="Product search"
        >
          <form className="site-header__search-form" onSubmit={handleSearchSubmit}>
            <label
              className="site-header__visually-hidden"
              htmlFor="site-search-input"
            >
              Search the shop
            </label>
            <SearchIcon />
            <input
              ref={searchInputRef}
              id="site-search-input"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search cheese, gifts, pantry and more"
              autoComplete="off"
            />
            <button className="site-header__search-submit" type="submit">
              Search
            </button>
            <button
              className="site-header__search-close"
              type="button"
              aria-label="Close search"
              onClick={() => setSearchOpen(false)}
            >
              <CloseIcon />
            </button>
          </form>
        </section>
      )}

      {mobileOpen && (
        <nav
          className="site-header__mobile-nav"
          id="mobile-navigation"
          aria-label="Mobile navigation"
        >
          <div className="site-header__mobile-heading">
            <span>Explore the market</span>
            <button
              type="button"
              aria-label="Close navigation"
              onClick={() => setMobileOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>
          <ul>
            {siteConfig.nav.map((item) => (
              <li key={item.href}>
                <Link to={item.href} onClick={handleNavigation}>
                  {item.label}
                  <span aria-hidden="true">→</span>
                </Link>
              </li>
            ))}
            {siteConfig.policyNav.map((item) => (
              <li key={item.href}>
                <Link to={item.href} onClick={handleNavigation}>
                  {item.label}<span aria-hidden="true">→</span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="site-header__mobile-utilities">
            <Link to="/shop?wishlist=true" onClick={handleNavigation}>
              Wishlist <span>{wishlistCount}</span>
            </Link>
            <Link to="/cartpage" onClick={handleNavigation}>
              Shopping cart <span>{cartCount}</span>
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export default Header;
